import React from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

interface PaginationProps {
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  className?: string;
  children?: React.ReactNode;
}

interface PaginationItemProps {
  active?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
  className?: string;
}

const PaginationItem: React.FC<PaginationItemProps> = ({ 
  active = false, 
  disabled = false, 
  onClick, 
  children, 
  className = '' 
}) => {
  const baseClasses = 'px-3 py-2 text-sm font-medium border transition-colors duration-200';
  const activeClasses = active 
    ? 'bg-primary text-white border-primary' 
    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50';
  const disabledClasses = disabled 
    ? 'opacity-50 cursor-not-allowed' 
    : 'cursor-pointer';

  return (
    <button
      className={`${baseClasses} ${activeClasses} ${disabledClasses} ${className}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
};

const Pagination: React.FC<PaginationProps> & {
  Item: React.FC<PaginationItemProps>;
  Prev: React.FC<{ onClick?: () => void; disabled?: boolean }>;
  Next: React.FC<{ onClick?: () => void; disabled?: boolean }>;
  First: React.FC<{ onClick?: () => void; disabled?: boolean }>;
  Last: React.FC<{ onClick?: () => void; disabled?: boolean }>;
} = ({ currentPage, totalPages, onPageChange, className = '', children }) => {
  // If children are provided, render them (React Bootstrap style)
  if (children) {
    return (
      <nav className={`flex items-center justify-center space-x-1 ${className}`}>
        {children}
      </nav>
    );
  }

  // Otherwise, render with props (new style)
  if (!currentPage || !totalPages || !onPageChange) {
    return null;
  }

  const getVisiblePages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, currentPage - delta); 
         i <= Math.min(totalPages - 1, currentPage + delta); 
         i++) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  const visiblePages = totalPages > 1 ? getVisiblePages() : [1];

  return (
    <nav className={`flex items-center justify-center space-x-1 ${className}`}>
      <PaginationItem
        disabled={currentPage === 1}
        onClick={() => currentPage > 1 && onPageChange(currentPage - 1)}
        className="rounded-l-md"
      >
        <FaChevronLeft size={12} />
      </PaginationItem>

      {visiblePages.map((page, index) => (
        <PaginationItem
          key={index}
          active={page === currentPage}
          disabled={page === '...'}
          onClick={() => typeof page === 'number' && onPageChange(page)}
        >
          {page}
        </PaginationItem>
      ))}

      <PaginationItem
        disabled={currentPage === totalPages}
        onClick={() => currentPage < totalPages && onPageChange(currentPage + 1)}
        className="rounded-r-md"
      >
        <FaChevronRight size={12} />
      </PaginationItem>
    </nav>
  );
};

// Individual components for compatibility with React Bootstrap API
const PaginationPrev: React.FC<{ onClick?: () => void; disabled?: boolean }> = ({ onClick, disabled }) => (
  <PaginationItem disabled={disabled} onClick={onClick} className="rounded-l-md">
    <FaChevronLeft size={12} />
  </PaginationItem>
);

const PaginationNext: React.FC<{ onClick?: () => void; disabled?: boolean }> = ({ onClick, disabled }) => (
  <PaginationItem disabled={disabled} onClick={onClick} className="rounded-r-md">
    <FaChevronRight size={12} />
  </PaginationItem>
);

const PaginationFirst: React.FC<{ onClick?: () => void; disabled?: boolean }> = ({ onClick, disabled }) => (
  <PaginationItem disabled={disabled} onClick={onClick}>
    First
  </PaginationItem>
);

const PaginationLast: React.FC<{ onClick?: () => void; disabled?: boolean }> = ({ onClick, disabled }) => (
  <PaginationItem disabled={disabled} onClick={onClick}>
    Last
  </PaginationItem>
);

Pagination.Item = PaginationItem;
Pagination.Prev = PaginationPrev;
Pagination.Next = PaginationNext;
Pagination.First = PaginationFirst;
Pagination.Last = PaginationLast;

export default Pagination;